// Detecta plataforma de execução. Capacitor injeta a global window.Capacitor
// quando o app está empacotado e rodando nativamente (Android ou iOS).
//
// IMPORTANTE — Apple Guideline 3.1.1 (In-App Purchase):
// O Forjamente cobra subscriptions via gateway externo (Mercado Pago). Apple proíbe
// apps de oferecer compra de conteúdo digital fora do IAP. Pra evitar rejeição,
// no iOS escondemos toda menção a planos pagos, paywall e upgrade. Usuário iOS
// usa o app gratuitamente; pra fazer upgrade precisa abrir forjamente.com.br
// no navegador (mesma estratégia de Netflix, Spotify, Audible).

import { tlog } from './telemetry';

export type Platform = 'web' | 'ios' | 'android';

export function getPlatform(): Platform {
  // @ts-ignore — Capacitor injeta global em runtime
  const cap = typeof window !== 'undefined' ? (window as any).Capacitor : null;
  if (!cap || typeof cap.getPlatform !== 'function') return 'web';
  const p = cap.getPlatform();
  if (p === 'ios') return 'ios';
  if (p === 'android') return 'android';
  return 'web';
}

export const isIOSNativeApp = (): boolean => getPlatform() === 'ios';
export const isAndroidNativeApp = (): boolean => getPlatform() === 'android';
export const isNativeApp = (): boolean => getPlatform() !== 'web';

// O PLUGIN EXISTE NESTE APP INSTALADO? (pente fino 03/10/2026, DEV-03)
// O JS é o do site (sempre o mais novo), mas os plugins nativos são os do
// binário que a pessoa tem instalado. Num build antigo, ou num teste que
// simula o app, "App" e "PushNotifications" podem não existir: cada
// addListener virava uma promise rejeitada sem tratamento ("plugin is not
// implemented on ios"). Sem o plugin, vale o comportamento padrão do sistema.
// A ponte nativa injeta PluginHeaders no boot; o @capacitor/core (que traz o
// isPluginAvailable) só carrega junto do primeiro plugin, então ele vem depois.
export const temPlugin = (nome: string): boolean => {
  try {
    const cap = typeof window !== 'undefined' ? (window as any).Capacitor : null;
    if (!cap) return false;
    if (Array.isArray(cap.PluginHeaders)) return cap.PluginHeaders.some((h: any) => h?.name === nome);
    return typeof cap.isPluginAvailable === 'function' ? !!cap.isPluginAvailable(nome) : true;
  } catch { return false; }
};

// addListener devolve promise: se o plugin faltar mesmo assim, a recusa é tratada.
const ouvir = (plugin: any, evento: string, fn: (...a: any[]) => any): void => {
  try { Promise.resolve(plugin.addListener(evento, fn)).catch(() => { /* sem o plugin */ }); } catch { /* idem */ }
};

// Esconder paywall/upgrade no iOS pra cumprir Guideline 3.1.1.
export const shouldHidePaywall = (): boolean => false; // antes: isIOSNativeApp() — agora temos IAP via RevenueCat, paywall pode abrir interno

// ─────────────────────────────────────────────────────────────
// Abrir URL externa — Safari/Chrome do device, NÃO o WebView.
// ─────────────────────────────────────────────────────────────
// Crítico pra Apple 3.1.1: páginas de planos/checkout NÃO podem rodar dentro
// do WebView do app (Apple considera isso oferta de IAP fora do sistema deles).
// Em Capacitor, window.open(url, '_blank') é inconsistente — às vezes abre
// dentro do mesmo WebView. Solução: plugin @capacitor/browser que abre o
// SFSafariViewController (iOS) ou Custom Tabs (Android), ambos contam como
// "navegador do sistema" pra Apple.
//
// Uso:
//   import { openExternalUrl } from './v2/platform';
//   openExternalUrl('https://forjamente.com.br/planos');
export async function openExternalUrl(url: string): Promise<void> {
  if (typeof window === 'undefined') return;
  if (!isNativeApp()) {
    // Web: nova aba normal
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }
  // Native: usa plugin Browser (lazy import — não quebra build web)
  try {
    const mod: any = await import('@capacitor/browser');
    const Browser = mod.Browser;
    if (Browser?.open) {
      await Browser.open({ url, presentationStyle: 'popover' });
      return;
    }
  } catch {}
  // Fallback se plugin não tiver disponível (raro): tenta window.open
  window.open(url, '_blank', 'noopener,noreferrer');
}

// Registra device nativo no FCM via Capacitor PushNotifications plugin.
// Retorna { ok, token } ou { ok: false, error }. No-op em web (caller deve
// chamar isNativeApp() antes de invocar).
//
// IMPORTANTE: o plugin @capacitor/push-notifications resolve o FCM token
// via callback assíncrono. Esperamos até 8s pelo evento 'registration'.
export async function registerNativePush(apiBase: string, accessToken: string): Promise<{ ok: boolean; token?: string; error?: string }> {
  // Helper que dispatcha evento de progresso pro DiagnosticCard escutar
  const logStep = (label: string, ok: boolean | null, detail: string = '') => {
    try {
      window.dispatchEvent(new CustomEvent('forjamente:push-step', {
        detail: { label, ok, detail }
      }));
    } catch {}
    try { tlog('push:step', { label, ok, detail: detail.slice(0, 160) }); } catch {}
    console.log(`[push-step] ${ok === true ? '[OK]' : ok === false ? '[ERR]' : '[..]'} ${label}${detail ? ': ' + detail : ''}`);
  };
  logStep('isNativeApp check', isNativeApp());
  if (!isNativeApp()) return { ok: false, error: 'not_native' };
  if (!temPlugin('PushNotifications')) {
    logStep('Plugin disponivel', false, 'este app instalado nao tem o plugin de push — atualize pela loja');
    return { ok: false, error: 'plugin_missing' };
  }
  try {
    // Lazy import — só rola em runtime nativo, evita quebrar build web.
    const mod: any = await import('@capacitor/push-notifications');
    const PushNotifications = mod.PushNotifications;
    if (!PushNotifications) {
      logStep('Plugin disponivel', false, 'PushNotifications nao carregou');
      return { ok: false, error: 'plugin_missing' };
    }
    logStep('Plugin disponivel', true);

    const perm = await PushNotifications.requestPermissions();
    logStep('requestPermissions', perm.receive === 'granted', JSON.stringify(perm));
    if (perm.receive !== 'granted') {
      return { ok: false, error: 'permission_denied' };
    }

    // Promessa que resolve com o token assim que o evento 'registration' bate.
    // Timeout 30s — primeira execução em iOS pode demorar 10-20s pra Firebase
    // iOS SDK fazer o handshake completo: APNs → method swizzle do
    // didRegisterForRemoteNotifications → Firebase Messaging.apnsToken →
    // POST pra FCM API → retorno do FCM token. 8s era apertado demais.
    const tokenPromise = new Promise<string>((resolve, reject) => {
      let resolved = false;
      const timer = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          reject(new Error('timeout aguardando FCM token (30s) — Firebase pode levar até 30s na primeira execução'));
        }
      }, 30000);

      PushNotifications.addListener('registration', (info: any) => {
        if (resolved) return;
        resolved = true;
        clearTimeout(timer);
        resolve(info?.value || info?.token || '');
      });
      PushNotifications.addListener('registrationError', (e: any) => {
        if (resolved) return;
        resolved = true;
        clearTimeout(timer);
        reject(new Error(e?.error || 'registration_error'));
      });
    });

    logStep('Chamando PN.register()', null, 'aguardando token...');
    await PushNotifications.register();
    let fcmToken: string;
    try {
      fcmToken = await tokenPromise;
      logStep('FCM token recebido', true, fcmToken.slice(0, 25) + '...');
    } catch (e: any) {
      logStep('FCM token timeout/erro', false, e?.message || String(e));
      throw e;
    }
    if (!fcmToken) {
      logStep('FCM token vazio', false);
      return { ok: false, error: 'empty_token' };
    }

    const platform = getPlatform(); // 'android' | 'ios'
    const res = await fetch(`${apiBase}/push/fcm-register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        token: fcmToken,
        platform,
        deviceInfo: {
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        },
      }),
    });
    if (!res.ok) {
      const errText = await res.text().catch(() => '');
      logStep('POST /api/push/fcm-register', false, `status ${res.status} ${errText.slice(0, 100)}`);
      return { ok: false, error: `register failed: ${res.status}` };
    }
    const regResult = await res.json().catch(() => ({}));
    logStep('POST /api/push/fcm-register', true, `ok activeTokens=${regResult.activeTokens || '?'}`);

    // NOTA: nao usar PushNotifications.removeAllListeners() — eh undefined
    // em iOS Capacitor 6 (issue #7435), dispara exception silenciosa e
    // aborta o fluxo, fazendo addListener seguinte nao registrar.
    // Como o registerNativePush soh roda quando user clica 'Ativar' ou
    // 'Atualizar', e cada chamada cria listeners novos, podem ter
    // duplicatas — toleravel e ainda melhor que listener nenhum.

    // NOTA: os listeners de TAP (pushNotificationActionPerformed) e de notif em
    // FOREGROUND (pushNotificationReceived) agora são registrados UMA vez no
    // boot via setupPushTapListener() (chamado no App.tsx), não aqui — pra
    // matar o "tap morto" (antes o tap só funcionava DEPOIS de apertar 'Ativar')
    // e evitar listeners duplicados (removeAllListeners é proibido no iOS,
    // issue #7435).

    logStep('Registro completo', true, 'pronto pra receber pushes');
    return { ok: true, token: fcmToken };
  } catch (e: any) {
    logStep('Exception geral', false, e?.message || 'unknown');
    return { ok: false, error: e?.message || 'unknown' };
  }
}

// ─────────────────────────────────────────────────────────────
// Deep links nativos — Capacitor App.appUrlOpen
// ─────────────────────────────────────────────────────────────
// Quando o user clica num link https://forjamente.com.br/c/<token> e o app já
// está instalado (Android App Links / iOS Universal Links), o OS abre o app
// e dispara o evento 'appUrlOpen'. A gente pega a URL e navega o WebView
// pro path correspondente (que é tratado pelo SPA via /c/:token landing).
//
// Também trata custom scheme forjamente://c/<token> (fallback in-app browsers).
export function setupDeepLinkListener(): void {
  if (!isNativeApp() || !temPlugin('App')) return;
  // Lazy import pra não quebrar build web.
  import('@capacitor/app').then((mod: any) => {
    const App = mod.App;
    if (!App?.addListener) return;
    // Resume nativo: no iOS (WKWebView) o ciclo suspend/resume do app pelo
    // app-switcher pode NÃO emitir document.visibilitychange='visible' (o
    // documento nunca foi marcado hidden pelo WebKit, só o processo suspenso).
    // Re-emitimos um visibilitychange sintético no resume pra que TODOS os
    // handlers já existentes (refresh de token, pollers sociais, badge de
    // notificação) atualizem ao voltar pro app. No web/PWA o visibilitychange
    // nativo já cobre — aqui é só robustez extra pro nativo.
    ouvir(App, 'appStateChange', (state: { isActive: boolean }) => {
      try {
        if (state?.isActive && typeof document !== 'undefined') {
          document.dispatchEvent(new Event('visibilitychange'));
        }
      } catch { /* no-op */ }
    });
    ouvir(App, 'appUrlOpen', (data: { url: string }) => {
      try {
        if (!data?.url) return;
        let path = '';
        // Custom scheme: forjamente://c/<token> ou forjamente:c/<token>
        if (/^forjamente:/i.test(data.url)) {
          // Extrai a parte após o scheme. Ex: forjamente://c/abc → /c/abc
          const m = data.url.match(/^forjamente:(?:\/\/)?(.*)$/i);
          if (m && m[1]) path = '/' + m[1].replace(/^\/+/, '');
        } else {
          // HTTPS: pega path + search da URL completa (App Links / Universal Links).
          const u = new URL(data.url);
          path = u.pathname + (u.search || '');
        }
        if (!path || path === '/') return;
        // Hardening anti-hijack: garante que `path` é um caminho relativo
        // same-origin antes de navegar. Sem isso, um link forjamente://\evil.com
        // (ou //evil.com) seria interpretado como outra origem pelo parser de URL
        // e sequestraria o WebView AUTENTICADO pra um domínio do atacante.
        // Qualquer app no device pode disparar um forjamente:// — então validamos.
        path = path.replace(/\\/g, '/');
        let safePath = '';
        try {
          const u2 = new URL(path, window.location.origin);
          if (u2.origin !== window.location.origin) return;
          safePath = u2.pathname + (u2.search || '');
        } catch { return; }
        if (!safePath || safePath === '/') return;
        // Navega o WebView (mesma origem). Usa replace pra não criar entrada
        // extra no histórico nativo.
        if (typeof window !== 'undefined') {
          window.location.replace(safePath);
        }
      } catch {
        // URL inválida — ignora.
      }
    });
  }).catch(() => { /* plugin missing — no-op */ });
}

// ─────────────────────────────────────────────────────────────
// Tap em notificação (push remoto OU local re-exibida em foreground) → tela.
// ─────────────────────────────────────────────────────────────
// Navega o app a partir da `url` que o servidor coloca no data{} da notif.
// 1) Hardening same-origin (igual ao deep-link): só navega pra forjamente.com.br,
//    nunca pra outra origem (data{} vem do nosso servidor, mas validamos sempre).
// 2) Tenta navegação SPA SUAVE: dispara 'forjamente:navigate' (cancelable). O
//    App.tsx trata com react-router (navigate) e chama preventDefault. Se ninguém
//    tratar (app ainda não montou — cold start), cai pro window.location.href
//    (carga completa, aceitável no cold start). Assim o tap NUNCA fica morto.
function navigateFromNotification(rawUrl?: string): void {
  if (!rawUrl || typeof window === 'undefined') return;
  let safePath = '';
  try {
    const u = new URL(String(rawUrl).replace(/\\/g, '/'), window.location.origin);
    if (u.origin !== window.location.origin) return; // bloqueia outra origem
    safePath = u.pathname + (u.search || '');
  } catch { return; }
  if (!safePath) return;
  let handledViaSpa = false;
  try {
    const ev = new CustomEvent('forjamente:navigate', { detail: { path: safePath }, cancelable: true });
    // dispatchEvent devolve false se algum listener deu preventDefault (= SPA tratou).
    handledViaSpa = (window.dispatchEvent(ev) === false);
  } catch {}
  if (!handledViaSpa) {
    try { window.location.href = safePath; } catch {}
  }
}

// Registra os listeners de TAP e de notificação em FOREGROUND UMA ÚNICA VEZ, no
// boot do app (App.tsx chama isto). Antes esses listeners viviam dentro de
// registerNativePush, que só roda ao apertar 'Ativar' — então um tap com o app
// fechado podia ficar MORTO (só abria a home), e cada clique em 'Ativar'
// empilhava listeners (removeAllListeners é proibido no iOS, issue #7435).
let pushTapListenerSet = false;
export function setupPushTapListener(): void {
  if (!isNativeApp() || pushTapListenerSet || !temPlugin('PushNotifications')) return;
  pushTapListenerSet = true;
  // Tap em push remoto → navega pelo data.url.
  import('@capacitor/push-notifications').then((mod: any) => {
    const PushNotifications = mod.PushNotifications;
    if (!PushNotifications?.addListener) return;
    ouvir(PushNotifications, 'pushNotificationActionPerformed', (action: any) => {
      navigateFromNotification(action?.notification?.data?.url);
    });
    // Notif recebida em FOREGROUND: re-exibe via LocalNotifications (senão o
    // Android entrega só pro listener, sem banner) e avisa o app (badge +
    // teste de notificação do onboarding, que escuta 'forjamente:push-received').
    ouvir(PushNotifications, 'pushNotificationReceived', async (notification: any) => {
      try {
        window.dispatchEvent(new CustomEvent('forjamente:push-received', {
          detail: { title: notification?.title, body: notification?.body, data: notification?.data }
        }));
      } catch {}
      try {
        const lnMod: any = await import('@capacitor/local-notifications');
        const LN = lnMod.LocalNotifications;
        if (!LN) return;
        const perm = await LN.checkPermissions();
        if (perm.display !== 'granted') {
          const req = await LN.requestPermissions();
          if (req.display !== 'granted') return;
        }
        const uniqueId = Math.floor((Date.now() + Math.random() * 1000) % 1_000_000_000);
        await LN.schedule({
          notifications: [{
            id: uniqueId,
            title: notification?.title || 'Forjamente',
            body: notification?.body || '',
            extra: notification?.data || {},
          }],
        });
      } catch (e: any) {
        console.error('[push] erro ao agendar LocalNotification:', e?.message || e);
      }
    });
  }).catch(() => { /* plugin missing — no-op */ });
  // Tap na LocalNotification re-exibida em foreground → também navega.
  import('@capacitor/local-notifications').then((mod: any) => {
    const LN = mod.LocalNotifications;
    if (!LN?.addListener) return;
    ouvir(LN, 'localNotificationActionPerformed', (action: any) => {
      navigateFromNotification(action?.notification?.extra?.url);
    });
  }).catch(() => { /* plugin missing — no-op */ });
}

// ─────────────────────────────────────────────────────────────
// O BOTÃO VOLTAR DO ANDROID
// ─────────────────────────────────────────────────────────────
// Sem um ouvinte de `backButton`, o Capacitor deixa valer o comportamento
// padrão do Android: percorrer o histórico do WebView ou, se não houver,
// ENCERRAR A ACTIVITY. Como quase todo modal do app é estado do React e não
// mexe na URL, o voltar fazia uma de duas coisas erradas:
//
//   · logo depois de abrir o app, sem histórico: fechava o app inteiro, com o
//     modal ainda aberto no estado;
//   · com histórico: navegava a tela DE BAIXO, e o modal continuava por cima,
//     agora sobre o conteúdo errado.
//
// Em nenhum dos dois o modal fechava — que é a única coisa que qualquer pessoa
// no Android espera daquele botão.
//
// O desenho: uma PILHA de fechadores. Quem abre algo empilha o seu fechador e
// desempilha ao fechar. O voltar fecha o do topo. Sem nada empilhado, entrega
// para quem cuida da navegação do app; e só quando não há mais para onde
// voltar é que o app sai — aí sim, como o Android manda.
const fechadores: Array<{ id: number; fechar: () => void }> = [];
let proximoFechador = 1;

/** Há alguma folha/modal/editor aberto agora? (pente fino, UX-28) */
export const temFolhaAberta = (): boolean => fechadores.length > 0;

/** Registra algo que o voltar deve fechar. Devolve a função de desfazer. */
export function aoVoltarFechar(fechar: () => void): () => void {
  const id = proximoFechador++;
  fechadores.push({ id, fechar });
  agendarSincronia();
  return () => {
    const i = fechadores.findIndex((f) => f.id === id);
    if (i >= 0) fechadores.splice(i, 1);
    agendarSincronia();
  };
}

// ─────────────────────────────────────────────────────────────
// O VOLTAR DO NAVEGADOR (web) — a mesma pilha (pente fino, QA-03)
// ─────────────────────────────────────────────────────────────
// No site, com uma folha aberta ("Adicionar livro", "Registrar"…), o voltar
// do navegador ou do Android trocava a página DE BAIXO e a folha continuava
// por cima. A pilha acima só servia ao app nativo.
//
// Aqui cada fechador da pilha ganha uma entrada no histórico (mesma URL,
// marcada), e o voltar que tira uma dessas entradas fecha a folha do topo —
// a página de baixo fica onde está. Quando a folha fecha pelo próprio botão,
// a entrada sobrando sai do histórico, para o próximo voltar não ser morto.
//
// A conta é feita DEPOIS de cada mudança (setTimeout 0), não a cada
// registro: o App desfaz e refaz todos os fechadores a cada folha que abre,
// e empilhar/desempilhar no meio disso brigaria com o próprio navegador.
const MARCA_FOLHA = '__folhaPf';
let ouvindoNavegador = false;
let naPilha = 0;       // entradas de folha que empilhamos sobre a página atual
let ignorarPops = 0;   // popstates causados por nós mesmos
let sincroniaMarcada = false;

const marcaAtual = (): number => {
  try { const st: any = window.history.state; return (st && Number(st[MARCA_FOLHA])) || 0; } catch { return 0; }
};

function agendarSincronia() {
  if (!ouvindoNavegador || sincroniaMarcada) return;
  sincroniaMarcada = true;
  setTimeout(sincronizarHistorico, 0);
}

function sincronizarHistorico() {
  sincroniaMarcada = false;
  if (!ouvindoNavegador) return;
  const quer = fechadores.length;
  if (quer > naPilha) {
    for (let k = naPilha; k < quer; k++) {
      const st: any = window.history.state || {};
      window.history.pushState({ ...st, [MARCA_FOLHA]: k + 1 }, '', window.location.href);
    }
    naPilha = quer;
  } else if (quer < naPilha) {
    const marca = marcaAtual();
    // A folha fechou porque a pessoa navegou para outra tela: a entrada de
    // cima já não é nossa. Voltar aqui levaria a pessoa embora da tela nova.
    if (!marca) { naPilha = quer; return; }
    const n = Math.min(naPilha - quer, marca);
    naPilha = quer;
    if (n > 0) { ignorarPops++; window.history.go(-n); }
  }
}

/** Liga o voltar do navegador à pilha de fechadores. Só na web. */
export function ligarVoltarDoNavegador(): void {
  if (ouvindoNavegador || isNativeApp() || typeof window === 'undefined') return;
  ouvindoNavegador = true;
  window.addEventListener('popstate', () => {
    if (ignorarPops > 0) { ignorarPops--; return; }
    const marca = marcaAtual();
    if (naPilha > marca) {
      // Saiu de uma entrada de folha: fecha a(s) folha(s) do topo.
      const quantas = naPilha - marca;
      naPilha = marca;
      const topo = fechadores.slice(-quantas).reverse();
      for (const f of topo) { try { f.fechar(); } catch (e) { /* segue */ } }
    } else if (marca > 0 && !fechadores.length) {
      // Entrada de folha que sobrou de uma navegação com a folha aberta:
      // pula, senão este voltar não faria nada na tela.
      ignorarPops++;
      window.history.back();
    }
  });
  agendarSincronia();
}

export async function setupBackButtonHandler(voltarNoApp: () => boolean) {
  if (!isNativeApp() || !temPlugin('App')) return;
  try {
    const { App } = await import('@capacitor/app');
    ouvir(App, 'backButton', () => {
      // 1. O que estiver por cima fecha primeiro.
      const topo = fechadores[fechadores.length - 1];
      if (topo) { try { topo.fechar(); } catch (e) {} return; }
      // 2. Depois, a navegação do próprio app. `voltarNoApp` devolve false
      //    quando já está na raiz e não há para onde voltar.
      let voltou = false;
      try { voltou = voltarNoApp(); } catch (e) { voltou = false; }
      if (voltou) return;
      // 3. Só então o app sai — que é o que o Android espera na raiz.
      try { App.exitApp(); } catch (e) {}
    });
  } catch (e) { /* sem plugin: o padrão do sistema segue valendo */ }
}
